import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, Download, Bookmark, BookmarkCheck, Loader2, FileText, FolderArchive, ExternalLink,
} from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RiskBadge } from "@/components/risk/RiskBadge";
import { EmptyState } from "@/components/common/EmptyState";
import { EvidenceBanner } from "@/components/dashboard/EvidenceBanner";
import api, { getErrorMessage, downloadReportPdf } from "@/lib/api";

export default function EvidenceCentre() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [band, setBand] = useState("All");
  const [savedOnly, setSavedOnly] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (band !== "All") params.risk_band = band;
      if (savedOnly) params.saved_only = true;
      const r = await api.get("/reports", { params });
      setReports(r.data || []);
    } catch (e) {
      toast.error(getErrorMessage(e, "Could not load reports."));
    } finally {
      setLoading(false);
    }
  }, [q, band, savedOnly]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const toggleSaved = async (id) => {
    try {
      const r = await api.post(`/assessments/${id}/save`);
      setReports((prev) => prev.map((x) => (x.id === id ? { ...x, saved: r.data.saved } : x)));
      toast.success(r.data.saved ? "Report saved" : "Removed from saved");
    } catch (e) {
      toast.error(getErrorMessage(e, "Could not update report."));
    }
  };

  const download = async (id, initials) => {
    setDownloading(id);
    try {
      await downloadReportPdf(id, `hostguard-evidence-${(initials || "report").replace(/[^a-zA-Z0-9]/g, "")}.pdf`);
      toast.success("Evidence report downloaded");
    } catch (e) {
      toast.error(getErrorMessage(e, "Could not generate the PDF."));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <AppShell title="Evidence Centre">
      <div className="space-y-5" data-testid="evidence-centre">
        <div className="flex items-center gap-2">
          <FolderArchive className="h-5 w-5 text-primary" />
          <p className="text-sm text-muted-foreground">All your assessment reports in one place — search, save and download as branded PDF evidence.</p>
        </div>

        <EvidenceBanner />

        {/* Filters */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by guest initials, property or platform…"
              className="pl-9"
              data-testid="evidence-search"
            />
          </div>
          <Select value={band} onValueChange={setBand}>
            <SelectTrigger className="w-full sm:w-44" data-testid="evidence-filter-band"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["All", "Low", "Medium", "High"].map((b) => <SelectItem key={b} value={b}>{b === "All" ? "All risk bands" : `${b} risk`}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            variant={savedOnly ? "default" : "outline"}
            onClick={() => setSavedOnly((v) => !v)}
            className="gap-2"
            data-testid="evidence-saved-toggle"
          >
            <Bookmark className="h-4 w-4" /> Saved only
          </Button>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : reports.length === 0 ? (
          <EmptyState
            title="No reports found"
            description="Run an assessment to generate evidence reports, or adjust your search filters."
            action={<Link to="/app/new"><Button>New assessment</Button></Link>}
          />
        ) : (
          <div className="space-y-3" data-testid="evidence-report-list">
            {reports.map((r) => (
              <div key={r.id} className="flex flex-col gap-3 rounded-xl border bg-card p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`evidence-row-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <p className="truncate text-sm font-medium">
                      {r.guest_initials || "—"} · <span className="text-muted-foreground">{r.platform}</span>
                      {r.saved && <Badge variant="secondary" className="ml-2 text-[10px]">Saved</Badge>}
                    </p>
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {r.property_name || "No property"} · {new Date(r.created_at).toLocaleString()}
                    {r.report_generated_count > 0 ? ` · ${r.report_generated_count} PDF download(s)` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-display text-lg font-semibold tabular-nums">{r.overall_score}</span>
                  <RiskBadge band={r.risk_band} size="sm" />
                  <Link to={`/app/result/${r.id}`}>
                    <Button variant="ghost" size="icon" title="Open report" data-testid={`evidence-open-${r.id}`}><ExternalLink className="h-4 w-4" /></Button>
                  </Link>
                  <Button variant="ghost" size="icon" onClick={() => toggleSaved(r.id)} title={r.saved ? "Unsave" : "Save"} data-testid={`evidence-save-${r.id}`}>
                    {r.saved ? <BookmarkCheck className="h-4 w-4 text-primary" /> : <Bookmark className="h-4 w-4" />}
                  </Button>
                  <Button size="sm" className="gap-1.5" disabled={downloading === r.id} onClick={() => download(r.id, r.guest_initials)} data-testid={`evidence-download-${r.id}`}>
                    {downloading === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} PDF
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
